<script setup lang="ts">
/**
 * Подтверждение первого добавления конкретного SKU в режиме «Предупреждать».
 * Показывается только по серверному решению confirmation_required (store.pendingConfirmation).
 * Ответ уходит в store → сервер; сам диалог ничего не разрешает.
 */
import { computed, nextTick, ref, watch } from "vue";
import type { SelectionReason } from "@/api/nutrition";
import { useNutritionSelectionStore } from "@/stores/nutrition-selection";

const store = useNutritionSelectionStore();
const pending = computed(() => store.pendingConfirmation);
const yesBtn = ref<HTMLButtonElement | null>(null);
let returnFocus: HTMLElement | null = null;

const KIND: Record<SelectionReason["kind"], string> = { contains: "содержит", traces: "может содержать", unknown: "состав неизвестен" };
const reasonLines = computed(() => {
  const groups = new Map<SelectionReason["kind"], string[]>();
  for (const r of pending.value?.reasons ?? []) {
    const list = groups.get(r.kind) ?? [];
    if (!list.includes(r.value)) list.push(r.value);
    groups.set(r.kind, list);
  }
  return (["contains", "traces", "unknown"] as const).filter((k) => groups.has(k)).map((k) => `${KIND[k]}: ${groups.get(k)!.join(", ")}`);
});

watch(pending, async (p, prev) => {
  if (p && !prev) {
    returnFocus = (typeof document !== "undefined" ? document.activeElement : null) as HTMLElement | null;
    await nextTick();
    yesBtn.value?.focus();
  } else if (!p && prev) {
    returnFocus?.focus?.();
    returnFocus = null;
  }
});

function answer(v: boolean) { void store.answerConfirmation(v); }
function onKey(e: KeyboardEvent) {
  if (e.key === "Escape") { e.preventDefault(); answer(false); return; }
  if (e.key !== "Tab") return;
  const root = e.currentTarget as HTMLElement;
  const f = [...root.querySelectorAll<HTMLElement>("button")];
  if (!f.length) return;
  const i = f.indexOf(document.activeElement as HTMLElement);
  if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
  else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
}
</script>

<template>
  <div v-if="pending" class="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center">
    <div
      role="dialog" aria-modal="true" aria-labelledby="ps-confirm-title" aria-describedby="ps-confirm-text"
      class="w-full max-w-md rounded-vd-card bg-vd-card p-4 shadow-float space-y-3 max-h-[90vh] overflow-y-auto"
      @keydown="onKey"
    >
      <h2 id="ps-confirm-title" class="text-lg font-bold text-vd-ink">Добавить этот товар?</h2>
      <div id="ps-confirm-text" class="space-y-2 text-sm text-vd-ink">
        <p class="font-semibold">{{ pending.title }}</p>
        <ul v-if="reasonLines.length" class="rounded-vd-tile px-3 py-2" style="background: var(--vd-hit-bg); color: var(--vd-hit-ink)">
          <li v-for="l in reasonLines" :key="l">{{ l }}</li>
        </ul>
        <p v-else>Товар может не подходить под ваши ограничения.</p>
        <p class="text-xs text-[color:var(--vd-ink-2)]">
          Подтверждение относится только к этому товару в его текущем составе и действует до конца сессии. Профиль не меняется.
        </p>
      </div>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" data-testid="ps-confirm-no" class="vd-btn-secondary min-h-[44px]" @click="answer(false)">Отменить добавление</button>
        <button ref="yesBtn" type="button" data-testid="ps-confirm-yes" class="vd-btn-primary min-h-[44px]" @click="answer(true)">Добавить этот товар</button>
      </div>
    </div>
  </div>
</template>
